// Component to allow event sounds to be heard even when not on the Connect View
import {Howl} from 'howler';
import {uniq} from 'lodash';
import {useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import {useConfiguration, useConfigurations} from '../../hooks/configuration';
import {useAppSelector} from '../../hooks/redux';
import {EventSoundResponse} from '../../interfaces/Event';
import reduxActions from '../../redux/actions';
import {useGetEventSoundQuery} from '../../redux/app/api/eventSummaryApi';
import reduxSelectors from '../../redux/selectors';
import {CONNECT_EVENT_SOUNDS, HAZARD_AI_EVENT_SOUNDS} from '../../utils/events';
import {SOUNDS} from '../../utils/sounds';
import {isPresent} from '../../utils/type-guards';

export const EventSummarySounds = () => {
  const reduxDispatch = useDispatch();
  const company = useAppSelector(({assets}) => assets.company);
  const configurations = useConfigurations();
  const lastEventIds = useAppSelector(({app}) => app.lastEventIds);
  const {eventSummaryData} = useGetEventSoundQuery(undefined, {
    selectFromResult: ({data}) => ({eventSummaryData: data}),
  });

  const newEventSoundConfiguration = useConfiguration(
    'commtrac',
    'sound_new_event'
  );

  const getSoundFilesFromEventSummary = (
    eventSummary: EventSoundResponse
  ) => {
    const soundNames: (string | number)[] = [];

    if (company?.commtrac_enabled) {
      const connectSounds = eventSummary.connect.sound
        .map((i) => {
          const configKey = CONNECT_EVENT_SOUNDS[i.type];
          return configKey ? configurations[configKey]?.value : null;
        })
        .filter(isPresent);
      soundNames.push(...connectSounds);
    }

    if (company?.ams_enabled) {
      const amsSounds = eventSummary.ams.sound
        .map((i) => {
          const configKey = CONNECT_EVENT_SOUNDS[i.type];
          return configKey ? configurations[configKey]?.value : null;
        })
        .filter(isPresent);
      soundNames.push(...amsSounds);
    }

    if (company?.hazard_ai_enabled) {
      const hazardAiSounds = eventSummary.hazard_ai.sound
        .map((i) => {
          const configKey = HAZARD_AI_EVENT_SOUNDS[i.type];
          return configKey ? configurations[configKey]?.value : null;
        })
        .filter(isPresent);
      soundNames.push(...hazardAiSounds);
    }

    if (company?.belt_enabled) {
      const beltSounds = eventSummary.belt.sound
        .map((i) => {
          const configKey = CONNECT_EVENT_SOUNDS[i.type];
          return configKey ? configurations[configKey]?.value : null;
        })
        .filter(isPresent);
      soundNames.push(...beltSounds);
    }

    if (company?.shaft_enabled) {
      const shaftSounds = eventSummary.shaft_clearance.sound
        .map((i) => {
          const configKey = CONNECT_EVENT_SOUNDS[i.type];
          return configKey ? configurations[configKey]?.value : null;
        })
        .filter(isPresent);
      soundNames.push(...shaftSounds);
    }

    let hasNewEvents = false;
    const newLastEventIds = {...lastEventIds};

    if (
      company?.commtrac_enabled &&
      eventSummary.connect.last_id &&
      eventSummary.connect.last_id > (lastEventIds.connect ?? 0)
    ) {
      hasNewEvents = true;
      newLastEventIds.connect = eventSummary.connect.last_id;
    }

    if (
      company?.ams_enabled &&
      eventSummary.ams.last_id &&
      eventSummary.ams.last_id > (lastEventIds.ams ?? 0)
    ) {
      hasNewEvents = true;
      newLastEventIds.ams = eventSummary.ams.last_id;
    }

    if (
      company?.hazard_ai_enabled &&
      eventSummary.hazard_ai.last_id &&
      eventSummary.hazard_ai.last_id > (lastEventIds.hazard_ai ?? 0)
    ) {
      hasNewEvents = true;
      newLastEventIds.hazard_ai = eventSummary.hazard_ai.last_id;
    }

    if (
      company?.belt_enabled &&
      eventSummary.belt.last_id &&
      eventSummary.belt.last_id > (lastEventIds.belt ?? 0)
    ) {
      hasNewEvents = true;
      newLastEventIds.belt = eventSummary.belt.last_id;
    }

    if (
      company?.shaft_enabled &&
      eventSummary.shaft_clearance.last_id &&
      eventSummary.shaft_clearance.last_id > (lastEventIds.shaft_clearance ?? 0)
    ) {
      hasNewEvents = true;
      newLastEventIds.shaft_clearance = eventSummary.shaft_clearance.last_id;
    }

    if (hasNewEvents) {
      soundNames.unshift(newEventSoundConfiguration?.value ?? '');
      reduxActions.app.setApp(reduxDispatch, {
        lastEventIds: newLastEventIds,
      });
    }

    return uniq(soundNames.map((i) => SOUNDS[i]).filter(isPresent));
  };

  const muteSounds = useAppSelector(reduxSelectors.app.getSoundMuteMode);
  const [soundFiles, setSoundFiles] = useState<any[]>([]);

  useEffect(() => {
    if (eventSummaryData && soundFiles.length === 0) {
      setSoundFiles(getSoundFilesFromEventSummary(eventSummaryData));
    }
  }, [eventSummaryData]);

  const sound = useMemo(() => {
    const src = soundFiles[0];
    return src ? new Howl({src}) : null;
  }, [soundFiles]);

  useEffect(() => {
    if (sound) {
      if (muteSounds) {
        return;
      }

      if (sound.state() === 'loaded') {
        sound.play();
      } else {
        sound.once('load', () => sound.play());
      }
      sound.once('end', () => {
        setSoundFiles(soundFiles.slice(1));
      });

      return () => {
        sound.unload();
      };
    }
  }, [sound]);

  return null;
};
